import axiosInstance from "../axiosConfig";

// Helper function to get vendors from localStorage
const getLocalVendors = () => {
  const vendors = localStorage.getItem("vendors");
  return vendors ? JSON.parse(vendors) : [];
};

// Helper function to save vendors to localStorage
const saveLocalVendors = (vendors) => {
  localStorage.setItem("vendors", JSON.stringify(vendors));
};

export const getAllVendors = async () => {
  try {
    // Try the API call first
    try {
      const response = await axiosInstance.get("/vendors");
      // Map the backend status to frontend active boolean
      const vendors = (response.data.data || response.data).map((vendor) => ({
        ...vendor,
        active: vendor.status === "active",
      }));
      return { success: true, data: vendors };
    } catch (apiError) {
      console.log("API not available, using localStorage instead");
      // Fallback to localStorage if API fails
      const localVendors = getLocalVendors();
      return { success: true, data: localVendors };
    }
  } catch (error) {
    console.error("Error in getAllVendors:", error);
    const localVendors = getLocalVendors();
    return { success: true, data: localVendors };
  }
};

export const getVendorById = async (id) => {
  try {
    // Try the API call first
    try {
      const response = await axiosInstance.get(`/vendors/${id}`);
      // Backend returns { success: true, data: {...} }
      return response.data.data || response.data;
    } catch (apiError) {
      // Fallback to localStorage if API fails
      const vendors = getLocalVendors();
      const vendor = vendors.find((v) => v._id === id);
      if (!vendor) throw new Error("Vendor not found");
      return vendor;
    }
  } catch (error) {
    console.error("Error in getVendorById:", error);
    throw error;
  }
};

export const createVendor = async (vendorData) => {
  try {
    // Try the API call first
    try {
      const response = await axiosInstance.post("/vendors", vendorData);
      // Map the backend status to frontend active boolean
      const vendor = response.data.data || response.data;
      return {
        ...vendor,
        active: vendor.status === "active",
      };
    } catch (apiError) {
      console.log("API not available, using localStorage instead");
      // Fallback to localStorage if API fails
      const vendors = getLocalVendors();
      const newVendor = {
        ...vendorData,
        _id: Date.now().toString(), // Generate a unique ID
        createdAt: new Date().toISOString(),
        active: vendorData.active,
      };
      vendors.push(newVendor);
      saveLocalVendors(vendors);
      return newVendor;
    }
  } catch (error) {
    console.error("Error in createVendor:", error);
    throw error;
  }
};

export const updateVendor = async (id, vendorData) => {
  try {
    // Try the API call first
    try {
      const response = await axiosInstance.put(`/vendors/${id}`, vendorData);
      // Map the backend status to frontend active boolean
      const vendor = response.data.data || response.data;
      return {
        ...vendor,
        active: vendor.status === "active",
      };
    } catch (apiError) {
      // Fallback to localStorage if API fails
      const vendors = getLocalVendors();
      const index = vendors.findIndex((v) => v._id === id);
      if (index === -1) throw new Error("Vendor not found");

      const updatedVendor = {
        ...vendors[index],
        ...vendorData,
        _id: id,
        updatedAt: new Date().toISOString(),
        active: vendorData.active,
      };

      vendors[index] = updatedVendor;
      saveLocalVendors(vendors);
      return updatedVendor;
    }
  } catch (error) {
    console.error("Error in updateVendor:", error);
    throw error;
  }
};

export const deleteVendor = async (id) => {
  try {
    // Try the API call first
    try {
      const response = await axiosInstance.delete(`/vendors/${id}`);
      return response.data;
    } catch (apiError) {
      // Fallback to localStorage if API fails
      const vendors = getLocalVendors();
      const filteredVendors = vendors.filter((v) => v._id !== id);
      if (filteredVendors.length === vendors.length)
        throw new Error("Vendor not found");

      saveLocalVendors(filteredVendors);
      return { success: true, message: "Vendor deleted successfully" };
    }
  } catch (error) {
    console.error("Error in deleteVendor:", error);
    throw error;
  }
};
